export default class CindyRelayClient {
  constructor({ getCredentials, onEvent }) {
    this.getCredentials = getCredentials;
    this.onEvent = onEvent || (() => {});
    this.socket = null;
    this.authenticated = false;
    this.stopped = false;
    this.connecting = null;
    this.retry = 0;
    this.retryTimer = null;
  }

  emit(message) {
    this.onEvent(message);
  }

  connect() {
    if (this.authenticated && this.socket && this.socket.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }
    if (this.connecting) return this.connecting;
    if (this.retryTimer) {
      window.clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    this.stopped = false;
    this.connecting = this.openSocket().finally(() => {
      this.connecting = null;
    });
    return this.connecting;
  }

  async openSocket() {
    const credentials = await this.getCredentials();
    if (!credentials || !credentials.ticket || !credentials.wsUrl) {
      throw new Error("后端未返回 Cindy 连接凭证");
    }

    return new Promise((resolve, reject) => {
      let settled = false;
      const ws = new WebSocket(credentials.wsUrl);
      this.socket = ws;
      const timeout = window.setTimeout(() => {
        if (!settled) {
          settled = true;
          reject(new Error("Cindy 连接认证超时"));
        }
        ws.close();
      }, 15000);

      ws.onopen = () => {
        ws.send(JSON.stringify({ type: "auth", role: "browser", ticket: credentials.ticket }));
      };
      ws.onmessage = event => {
        let message;
        try {
          message = JSON.parse(event.data);
        } catch (error) {
          this.emit({ type: "protocol_error", message: "收到无法解析的中转消息" });
          return;
        }
        if (message.type === "auth_ok") {
          this.authenticated = true;
          this.retry = 0;
          window.clearTimeout(timeout);
          if (!settled) {
            settled = true;
            resolve();
          }
        }
        this.emit(message);
      };
      ws.onerror = () => {
        if (!settled) {
          settled = true;
          window.clearTimeout(timeout);
          reject(new Error("无法连接 Cindy 中转服务"));
        }
      };
      ws.onclose = event => {
        window.clearTimeout(timeout);
        if (this.socket !== ws) return;
        this.socket = null;
        this.authenticated = false;
        this.emit({ type: "offline", code: event.code, reason: event.reason || "连接已断开" });
        if (!settled) {
          settled = true;
          reject(new Error(event.reason || "Cindy 连接已断开"));
        }
        this.scheduleReconnect();
      };
    });
  }

  scheduleReconnect() {
    if (this.stopped || this.retryTimer) return;
    const delay = Math.min(30000, 1000 * (2 ** this.retry));
    this.retry += 1;
    this.retryTimer = window.setTimeout(() => {
      this.retryTimer = null;
      this.connect().catch(() => {});
    }, delay);
  }

  async ask(message, history, requestId) {
    await this.connect();
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN || !this.authenticated) {
      throw new Error("Cindy 中转连接尚未就绪");
    }
    this.socket.send(JSON.stringify({
      type: "request",
      request_id: requestId,
      message,
      history,
    }));
  }

  cancel(requestId) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN && this.authenticated) {
      this.socket.send(JSON.stringify({ type: "cancel", request_id: requestId }));
    }
  }

  close() {
    this.stopped = true;
    this.authenticated = false;
    if (this.retryTimer) window.clearTimeout(this.retryTimer);
    this.retryTimer = null;
    if (this.socket) this.socket.close(1000, "page closed");
    this.socket = null;
  }
}
